import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Store, Trash2, ExternalLink, EyeOff, Eye } from "lucide-react";
import * as repository from "@/data/adminRepository";
import { api, isMockApi } from "@/lib/api";
import { formatDate } from "../../../lib/utils";

export default function AdminBusinessesPage() {
  const { getVendors, deleteVendor, saveVendor } = repository;
  const nav = useNavigate();
  const [refresh, setRefresh] = useState(0);
  const [remoteVendors, setRemoteVendors] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!isMockApi);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");

  useEffect(() => {
    if (isMockApi) return;
    let active = true;
    setLoading(true);
    setError("");
    api.listAdminBusinesses({ search: q, page: 1, limit: 100 }).then((result) => {
      if (!active) return;
      const items = Array.isArray(result) ? result : (result?.items || []);
      setRemoteVendors(items.map(toAdminListVendor));
      setMeta(result?.meta || null);
    }).catch((err) => active && setError(err?.message || "Could not load businesses.")).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [q, refresh]);

  const localVendors = useMemo(() => getVendors(), [refresh]);
  const vendors = isMockApi ? localVendors : remoteVendors;

  const filtered = vendors.filter(
    (v) =>
      (type === "all" || v.businessType === type) &&
      (q.trim() === "" ||
        (v.name || "").toLowerCase().includes(q.toLowerCase()) ||
        v.email.toLowerCase().includes(q.toLowerCase()) ||
        (v.slug || "").includes(q.toLowerCase()))
  );

  const handleDelete = (v) => {
    if (!window.confirm(`Delete ${v.name || v.email}? This removes all their orders and bookings too.`)) return;
    const action = isMockApi ? Promise.resolve(deleteVendor(v.id)) : api.deleteAdminBusiness(v.id);
    action.then(() => { setRefresh((r) => r + 1); toast.success(`${v.name || v.email} deleted`); }).catch((err) => toast.error(err?.message || "Could not delete business."));
  };

  const toggleDisable = (v) => {
    const nextStatus = v.disabled ? "active" : "suspended";
    const action = isMockApi ? Promise.resolve(saveVendor({ ...v, disabled: nextStatus === "suspended" })) : api.updateAdminBusinessStatus(v.id, nextStatus);
    action.then(() => { setRefresh((r) => r + 1); toast.success(nextStatus === "suspended" ? "Business disabled" : "Business enabled"); }).catch((err) => toast.error(err?.message || "Could not update business."));
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-ink-muted">Directory</div>
        <h1 className="mt-1 text-3xl font-display font-medium tracking-tight" data-testid="admin-businesses-title">
          All businesses
        </h1>
        <p className="mt-1 text-ink-secondary">
          {meta?.total ?? vendors.length} total · {vendors.filter((v) => v.status ? v.status === "active" && v.onboarded : v.onboarded).length} live
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or slug"
            className="input-field pl-10"
            data-testid="admin-businesses-search"
          />
        </div>
        {isMockApi && <select value={type} onChange={(e) => setType(e.target.value)} className="input-field sm:max-w-[180px]" data-testid="admin-businesses-type-filter">
          <option value="all">All types</option>
          <option value="restaurant">Restaurants</option>
          <option value="salon">Salons</option>
        </select>}
        <button type="button" className="btn-ghost" onClick={() => setRefresh((r) => r + 1)} data-testid="admin-businesses-refresh">Refresh</button>
      </div>

      {error && <div role="alert" className="card-surface p-4 text-sm text-red-300">{error}</div>}
      {loading && <div className="card-surface p-10 text-center text-sm text-ink-muted">Loading businesses…</div>}

      {!loading && filtered.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <Store className="h-8 w-8 mx-auto text-ink-muted" />
          <div className="mt-3 font-display text-lg">No businesses match</div>
          <p className="mt-1 text-ink-secondary text-sm">Try different filters.</p>
        </div>
      ) : !loading && (
        <div className="card-surface overflow-hidden">
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-widest text-ink-muted border-b border-line">
            <div className="col-span-4">Business</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Slug</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <ul className="divide-y divide-line">
            {filtered.map((v) => (
              <li
                key={v.id}
                className="px-4 sm:px-5 py-4 grid md:grid-cols-12 gap-3 items-center hover:bg-white/[0.02] transition-colors cursor-pointer"
                data-testid={`admin-vendor-row-${v.id}`}
                onClick={() => nav(`/admin/businesses/${v.id}`)}
              >
                <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                  <div className="h-11 w-11 rounded-lg bg-bg-elevated border border-line overflow-hidden flex-shrink-0 grid place-items-center">
                    {v.logo ? <img src={v.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-4 w-4 text-ink-muted" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {v.name || <span className="text-ink-muted">Untitled</span>}
                      {!v.onboarded && <span className="tag !text-[10px]">Onboarding</span>}
                      {v.disabled && <span className="tag !text-[10px] !text-red-300 !border-red-500/30 !bg-red-500/10">Disabled</span>}
                    </div>
                    <div className="text-xs text-ink-muted truncate">{v.email}</div>
                  </div>
                </div>
                <div className="md:col-span-2 text-sm capitalize">{v.businessType || "restaurant"}</div>
                <div className="md:col-span-2 text-sm font-mono text-ink-secondary truncate">
                  {v.slug ? `/${v.slug}` : "—"}
                </div>
                <div className="md:col-span-2 text-sm text-ink-secondary">{formatDate(v.createdAt)}</div>
                <div className="md:col-span-2 flex items-center gap-1 justify-start md:justify-end" onClick={(e) => e.stopPropagation()}>
                  {v.slug && (
                    <a
                      href={`/store/${v.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="h-8 w-8 grid place-items-center rounded-md text-ink-secondary hover:bg-white/5 hover:text-ink-primary transition-colors"
                      aria-label="Open storefront"
                      data-testid={`admin-vendor-open-${v.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    onClick={() => toggleDisable(v)}
                    className="h-8 w-8 grid place-items-center rounded-md text-ink-secondary hover:bg-white/5 hover:text-ink-primary transition-colors"
                    aria-label={v.disabled ? "Enable" : "Disable"}
                    data-testid={`admin-vendor-toggle-${v.id}`}
                  >
                    {v.disabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(v)}
                    className="h-8 w-8 grid place-items-center rounded-md text-ink-secondary hover:bg-red-500/10 hover:text-red-400 transition-colors"
                    aria-label="Delete"
                    data-testid={`admin-vendor-delete-${v.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function toAdminListVendor(restaurant) {
  return {
    ...restaurant,
    businessType: "restaurant",
    disabled: restaurant.status === "suspended",
    logo: restaurant.logoUrl || "",
    slug: restaurant.slug || "",
  };
}
